import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

export default function BankImport() {
  const { apiFetch } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [bankAccountId, setBankAccountId] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [importing, setImporting] = useState(false);
  const [parsing, setParsing] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    apiFetch('/api/accounts').then(setAccounts).catch(() => {});
  }, []);

  const bankAccounts = accounts.filter(a => a.type === 'asset');
  const contraAccounts = accounts.filter(a => a.type !== 'asset');

  const handleFileParse = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setError(''); setMessage(''); setParsing(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const result = await apiFetch('/api/bank-import/parse', {
        method: 'POST',
        body: formData,
        isFormData: true
      });
      setTransactions(result.transactions.map(tx => ({
        ...tx,
        selected: true,
        contra_account_id: '',
        bank_account_id: bankAccountId
      })));
      setMessage(`${result.count} transaktioner fundet.`);
    } catch (err) {
      setError(err.message);
    }
    setParsing(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const toggleAll = (selected) => {
    setTransactions(prev => prev.map(tx => ({ ...tx, selected })));
  };

  const updateTx = (idx, field, value) => {
    setTransactions(prev => prev.map((tx, i) => i === idx ? { ...tx, [field]: value } : tx));
  };

  const handleImport = async () => {
    setError(''); setMessage(''); setImporting(true);
    const toImport = transactions
      .filter(tx => tx.selected && tx.contra_account_id)
      .map(tx => ({
        date: tx.date,
        text: tx.text,
        amount: tx.amount,
        bank_account_id: tx.bank_account_id || bankAccountId,
        contra_account_id: tx.contra_account_id
      }));

    if (toImport.length === 0) {
      setError('V\u00e6lg mindst \u00e9n transaktion og tildel en modkonto.');
      setImporting(false);
      return;
    }

    try {
      const result = await apiFetch('/api/bank-import/import', {
        method: 'POST',
        body: { transactions: toImport }
      });
      setMessage(result.message);
      setTransactions([]);
    } catch (err) {
      setError(err.message);
    }
    setImporting(false);
  };

  const fmt = (n) => parseFloat(n).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const selectedCount = transactions.filter(tx => tx.selected && tx.contra_account_id).length;

  return (
    <div>
      <div className="page-header">
        <h1>Bankimport</h1>
      </div>

      {message && <div className="alert alert-success">{message}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <h3 style={{marginBottom: '1rem'}}>Upload bankkontoudtog</h3>
        <p style={{fontSize: '0.875rem', color: 'var(--gray-700)', marginBottom: '1rem'}}>
          Upload en CSV-fil fra din bank. Filen skal indeholde kolonner for dato, tekst og beløb.
          De fleste danske banker tilbyder CSV-eksport fra netbank.
        </p>

        <div className="form-row" style={{alignItems: 'flex-end'}}>
          <div className="form-group">
            <label>Bankkonto</label>
            <select className="form-control" value={bankAccountId}
              onChange={e => {
                setBankAccountId(e.target.value);
                setTransactions(prev => prev.map(tx => ({ ...tx, bank_account_id: e.target.value })));
              }}>
              <option value="">Vælg bankkonto</option>
              {bankAccounts.map(a => (
                <option key={a.id} value={a.id}>{a.account_number} - {a.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>CSV-fil</label>
            <input type="file" ref={fileRef} accept=".csv,.txt" onChange={handleFileParse}
              className="form-control" disabled={!bankAccountId || parsing} />
          </div>
        </div>
      </div>

      {transactions.length > 0 && (
        <div className="card" style={{marginTop: '1rem'}}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
            <h3>Transaktioner ({transactions.length})</h3>
            <div style={{display: 'flex', gap: '0.5rem'}}>
              <button className="btn btn-secondary btn-sm" onClick={() => toggleAll(true)}>Vælg alle</button>
              <button className="btn btn-secondary btn-sm" onClick={() => toggleAll(false)}>Fravælg alle</button>
            </div>
          </div>

          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th style={{width: '40px'}}></th>
                  <th>Dato</th>
                  <th>Tekst</th>
                  <th className="text-right">Beløb</th>
                  <th>Modkonto</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx, idx) => (
                  <tr key={idx} style={{opacity: tx.selected ? 1 : 0.5}}>
                    <td>
                      <input type="checkbox" checked={tx.selected}
                        onChange={e => updateTx(idx, 'selected', e.target.checked)} />
                    </td>
                    <td>{tx.date}</td>
                    <td style={{maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis'}}>{tx.text}</td>
                    <td className="text-right amount" style={{color: tx.amount >= 0 ? 'var(--success)' : 'var(--danger)'}}>
                      {fmt(tx.amount)}
                    </td>
                    <td>
                      <select className="form-control" value={tx.contra_account_id}
                        onChange={e => updateTx(idx, 'contra_account_id', e.target.value)}
                        style={{minWidth: '200px', fontSize: '0.85rem'}}>
                        <option value="">Vælg konto</option>
                        {contraAccounts.map(a => (
                          <option key={a.id} value={a.id}>{a.account_number} - {a.name}</option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{marginTop: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <span style={{fontSize: '0.875rem', color: 'var(--gray-700)'}}>
              {selectedCount} af {transactions.length} transaktioner klar til import
            </span>
            <button className="btn btn-primary" onClick={handleImport}
              disabled={selectedCount === 0 || importing}>
              {importing ? 'Importerer...' : `Importer ${selectedCount} transaktioner`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
